import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StaffService from '../services/staffService';
import { Staff } from '../types';

const StaffForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const initialStaffState: Staff = {
    username: '',
    fullName: '',
    role: 'staff', // Mặc định là nhân viên thường
    password: ''
  };

  const [staff, setStaff] = useState<Staff>(initialStaffState);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  useEffect(() => {
    if (isEditMode) {
      fetchStaff();
    }
  }, [id]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await StaffService.getById(Number(id));
      // Không hiển thị mật khẩu khi chỉnh sửa
      const staffData = { ...response.data, password: '' };
      setStaff(staffData);
      setError(null);
    } catch (err) {
      setError('Không thể tải thông tin nhân viên. Vui lòng thử lại sau.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setStaff({ ...staff, [name]: value });
  };

  const validateForm = () => {
    if (!staff.username || !staff.fullName || !staff.role) {
      setError('Vui lòng điền đầy đủ thông tin bắt buộc.');
      return false;
    }

    if (!isEditMode && !staff.password) {
      setError('Vui lòng nhập mật khẩu.');
      return false;
    }

    if (staff.password && staff.password !== confirmPassword) {
      setError('Mật khẩu và xác nhận mật khẩu không khớp.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Nếu đang chỉnh sửa và không nhập mật khẩu mới, loại bỏ trường password
      const staffData = { ...staff };
      if (isEditMode && !staffData.password) {
        delete staffData.password;
      }

      if (isEditMode) {
        await StaffService.update(Number(id), staffData);
      } else {
        await StaffService.create(staffData);
      }

      setError(null);
      navigate('/admin/staff');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Có lỗi xảy ra. Vui lòng thử lại sau.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">
                {isEditMode ? 'Chỉnh sửa thông tin nhân viên' : 'Thêm nhân viên mới'}
              </h4>
            </div>
            <div className="card-body">
              {error && <div className="alert alert-danger">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Tên đăng nhập <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control ${submitted && !staff.username ? 'is-invalid' : ''}`}
                        name="username"
                        value={staff.username}
                        onChange={handleInputChange}
                        disabled={isEditMode} // Không cho phép thay đổi username khi sửa
                        required
                      />
                      {submitted && !staff.username && (
                        <div className="invalid-feedback">Tên đăng nhập là bắt buộc</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Họ và tên <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control ${submitted && !staff.fullName ? 'is-invalid' : ''}`}
                        name="fullName"
                        value={staff.fullName}
                        onChange={handleInputChange}
                        required
                      />
                      {submitted && !staff.fullName && (
                        <div className="invalid-feedback">Họ và tên là bắt buộc</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Vai trò <span className="text-danger">*</span></label>
                      <select
                        className={`form-control ${submitted && !staff.role ? 'is-invalid' : ''}`}
                        name="role"
                        value={staff.role}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="staff">Nhân viên</option>
                        <option value="admin">Quản trị viên</option>
                      </select>
                      {submitted && !staff.role && (
                        <div className="invalid-feedback">Vai trò là bắt buộc</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>{isEditMode ? 'Mật khẩu mới' : 'Mật khẩu'} {!isEditMode && <span className="text-danger">*</span>}</label>
                      <input
                        type="password"
                        className={`form-control ${submitted && !isEditMode && !staff.password ? 'is-invalid' : ''}`}
                        name="password"
                        value={staff.password || ''}
                        onChange={handleInputChange}
                        required={!isEditMode}
                      />
                      {submitted && !isEditMode && !staff.password && (
                        <div className="invalid-feedback">Mật khẩu là bắt buộc</div>
                      )}
                      {isEditMode && (
                        <small className="form-text text-muted">Để trống nếu không muốn thay đổi mật khẩu</small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>{isEditMode ? 'Xác nhận mật khẩu mới' : 'Xác nhận mật khẩu'} {!isEditMode && <span className="text-danger">*</span>}</label>
                      <input
                        type="password"
                        className={`form-control ${submitted && staff.password && staff.password !== confirmPassword ? 'is-invalid' : ''}`}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required={!isEditMode || !!staff.password}
                      />
                      {submitted && staff.password && staff.password !== confirmPassword && (
                        <div className="invalid-feedback">Mật khẩu xác nhận không khớp</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-12">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        isEditMode ? 'Cập nhật' : 'Thêm mới'
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary ms-2"
                      onClick={() => navigate('/admin/staff')}
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffForm; 